import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Award,
  Target,
  Sparkles,
  Megaphone,
  CalendarCheck,
  MessageSquare,
  MessageCircle,
  Handshake,
  TrendingUp,
  Volume2,
  VolumeX,
  X,
  ChevronDown,
  HelpCircle,
  Calendar as CalIcon,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { trackFunnel } from "@/lib/funnel";

const PAGE = "for-coaches";
const WA_NUMBER = "919811960890";
const WA_PREFILL = "Hi, I saw your Elite Coaches page on BrandChilly and would love to understand how the system can work for me.";
const waHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_PREFILL)}`;

const VSL_VIDEO_URL = "/assets/dse-vsl.mp4";

// ============================================================
// A/B HEADLINE TEST — sticky per-visitor variant, tracked via funnel meta
// ============================================================
const AB_KEY = "bc_fc_ab_variant";
const HEADLINE_VARIANTS = [
  {
    id: "elite-system",
    node: (
      <>
        Stop Running <span className="text-zinc-500 line-through decoration-1">Random Acts of Marketing.</span>
        <span className="block mt-2">
          Start Deploying an <span className="c-red italic">Elite Client Acquisition System.</span>
        </span>
      </>
    ),
  },
  {
    id: "pipeline-fix",
    node: (
      <>
        Your Coaching Is Elite. <span className="text-zinc-500">Your Client Pipeline Isn’t.</span>
        <span className="block mt-2">
          Let’s Fix That — <span className="c-red italic">Done-For-You.</span>
        </span>
      </>
    ),
  },
  {
    id: "predictable-engine",
    node: (
      <>
        More Clients. <span className="c-red italic">Less Chasing.</span>
        <span className="block mt-2">
          Build a <span className="c-red italic">Predictable Client Engine</span> in 90 Days.
        </span>
      </>
    ),
  },
];

function pickVariant() {
  try {
    let v = localStorage.getItem(AB_KEY);
    if (!v || !HEADLINE_VARIANTS.some((x) => x.id === v)) {
      v = HEADLINE_VARIANTS[Math.floor(Math.random() * HEADLINE_VARIANTS.length)].id;
      localStorage.setItem(AB_KEY, v);
    }
    return v;
  } catch (e) {
    return HEADLINE_VARIANTS[0].id;
  }
}

// Ad-performance screenshots
const AD_SCREENSHOTS = [
  { url: "https://customer-assets.emergentagent.com/job_viral-framework-2/artifacts/7jswdl6p_Screenshot%202026-06-07%20at%201.10.35%E2%80%AFAM.png", label: "Lifetime · All-time campaign performance", metric: "722M+ Impressions · ₹5.04 Cr+ ad spend · 6.5L+ conversions" },
  { url: "https://customer-assets.emergentagent.com/job_viral-framework-2/artifacts/6hs8rlde_Screenshot%202026-06-07%20at%201.07.16%E2%80%AFAM.png", label: "Recent push · High-spend campaign window", metric: "255M+ Impressions · ₹1.56 Cr+ spent · 1.22L+ conversions" },
  { url: "https://customer-assets.emergentagent.com/job_viral-framework-2/artifacts/wufhzw0i_Screenshot%202026-06-07%20at%201.09.40%E2%80%AFAM.png", label: "Sustained scale · Mid-cycle results", metric: "86M+ Impressions · ₹84 L+ spent · 85K+ conversions" },
];

const AUTHORITY = [
  { value: "₹200 Cr+", label: "Client Revenue Generated", icon: TrendingUp },
  { value: "₹20 Cr+", label: "Paid Ad Spend Managed", icon: Target },
  { value: "Meta Partner", label: "Official Credential", icon: ShieldCheck },
  { value: "10 L+", label: "Qualified Leads Generated", icon: Sparkles },
  { value: "16 Years", label: "On-the-Ground Tenure", icon: Award },
];

const REFRAME = [
  { stat: "70%", title: "Buyers decide before they speak to you", body: "The 2026 buyer is researched, AI-assisted, and resistant to generic hype. They show up to a call already 70% sold — or 70% out." },
  { stat: "21%", title: "Increase in Cost per Lead, Every Year", body: "Reaching qualified buyers in Coaching, Education and B2B costs more every year. Sloppy targeting = silent bleed." },
  { stat: "0", title: "Old playbook architecture", body: "Post → hope → chase → repeat. That isn't a system; it's a gamble that resets to zero every calendar month." },
];

const STAGES = [
  { n: "01", icon: Target, title: "Precision Traffic", body: "Hyper-targeted Meta + Google ad spend. We buy market attention cleanly — not beg an unpredictable organic algorithm for it." },
  { n: "02", icon: Megaphone, title: "High-Converting Offers", body: "One clean page. One dominant promise. One singular irresistible next step — built on value reciprocity, not pressure." },
  { n: "03", icon: CalendarCheck, title: "Elite Calendar Bookings", body: "Intelligent filtering questions weed out tire-kickers and low-intent profiles before they ever touch your calendar." },
  { n: "04", icon: MessageSquare, title: "Automated Nurture", body: "Multi-channel WhatsApp + email flows that address the 80% of leads who aren't ready to buy on day one." },
  { n: "05", icon: Sparkles, title: "High-Value Strategy Sessions", body: "Frictionless entry where you deliver real clarity — earning the right to the next conversation by being useful first." },
  { n: "06", icon: Handshake, title: "Invitation, Not Pitch", body: "Shift from aggressive pitching to structural alignment invitations. The right clients say yes. The wrong ones disqualify themselves." },
];

const CLIENTS = [
  { name: "Booming Bulls Academy", revenue: "₹100 Cr+", note: "Revenue scaled" },
  { name: "EIFS", revenue: "₹75 Cr+", note: "Revenue scaled" },
  { name: "Knowledgewoods", revenue: "₹15 Cr+", note: "Revenue scaled" },
];

const PROBLEMS = [
  "Chasing prospects in the DMs and hoping they reply",
  "Dropping your price just to close someone who’s on the fence",
  "Posting content that earns likes but never a single inbound lead",
  "No real brand, no authority, no predictable pipeline",
];

const IS_THIS_YOU = [
  "You’re a coach or consultant with real, proven expertise",
  "You struggle to get consistent, good-quality clients",
  "You’ve tried posting, ads, and referrals — nothing sticks",
  "You know you need a system, but you don’t know where to start",
];

const FAQS = [
  { q: "What happens after I apply?",
    a: "You’ll land on a private calendar page to pick a slot for a 30-minute eligibility evaluation. Slots are released two weeks in advance, in 30-minute blocks during business hours (IST). You’ll get an instant confirmation email with the meeting link.",
    icon: CalIcon,
  },
  { q: "Why is this an eligibility evaluation and not a sales call?",
    a: "To maintain delivery quality we only partner with a small portfolio of coaches per quarter. Before any pricing or proposal conversation, our team reviews your fit — niche, proof, offer maturity and growth readiness. If you’re a fit, we go deep on the system. If you’re not, we tell you exactly that, and point you to the right next step.",
    icon: HelpCircle,
  },
  { q: "What if my content gets engagement but no business?",
    a: "This is the single most common gap we see. Likes are a vanity score, not a buying signal. The 6-stage system is designed precisely for this case — it converts the audience you already have into actual revenue by adding the missing infrastructure (offer, capture, nurture, qualify) underneath your existing content.",
    icon: MessageSquare,
  },
  { q: "I don’t have a big audience yet. Is this for me?",
    a: "Audience size is irrelevant if the system is dialled in. Several of our highest-paying clients came in with under 2,000 followers — they had product-market fit and an offer worth scaling. If you have proven expertise and at least one client testimonial, audience can be bought with precision paid traffic.",
    icon: Sparkles,
  },
];

// ============================================================
// CTA Button — navigates to /verify-your-eligibility
// ============================================================
function CtaButton({ testId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="c-btn-primary inline-flex items-center justify-center text-base md:text-lg px-7 md:px-10 py-4 md:py-5 w-full sm:w-auto"
      data-testid={testId}
      style={{ minWidth: 280, minHeight: 56 }}
    >
      Check If You Qualify <ArrowRight size={20} />
    </button>
  );
}

function CtaSubtext() {
  return (
    <p className="mt-3 text-xs md:text-sm c-muted italic" data-testid="fc-cta-subtext">
      Takes 60 seconds · Private slot opens on the next screen.
    </p>
  );
}

function WhatsAppFastLane({ testId }) {
  const onClick = () => {
    trackFunnel("whatsapp_fastlane_click", PAGE);
    try {
      if (window.fbq) window.fbq("track", "Contact", { content_name: "ForCoachesWA" });
      if (window.gtag) window.gtag("event", "contact", { method: "whatsapp" });
    } catch (e) { /* swallow */ }
  };
  return (
    <div className="mt-5 text-center">
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm w-full sm:w-auto border border-emerald-600/50 text-emerald-300 hover:bg-emerald-900/20 transition"
        style={{ borderRadius: 0, minHeight: 48, minWidth: 280 }}
        data-testid={testId}
      >
        <MessageCircle size={16} /> Prefer WhatsApp? Chat with us directly
      </a>
    </div>
  );
}

function SystemStage({ s, index }) {
  const Icon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 64, rotateX: -24 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative pl-14 md:pl-24 pb-12 md:pb-16"
      style={{ transformPerspective: 1100 }}
      data-testid={`fc-sysstage-${index}`}
    >
      <div className="absolute left-[18px] md:left-[30px] top-2 w-3.5 h-3.5 -translate-x-1/2 bg-red-600 border-2 border-black" style={{ boxShadow: "0 0 0 4px rgba(217,4,41,0.18)" }} />
      <div className="border border-white/10 p-6 md:p-9" style={{ borderRadius: 0, background: "linear-gradient(180deg,#151515 0%,#0a0a0a 100%)", boxShadow: "0 24px 60px -30px rgba(192,41,26,0.5)" }}>
        <div className="flex items-center gap-4 mb-3">
          <span className="text-3xl md:text-5xl c-red leading-none" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700 }}>{s.n}</span>
          <span className="flex items-center justify-center w-11 h-11 border border-red-700/40 flex-shrink-0"><Icon size={20} className="c-red" /></span>
        </div>
        <h3 className="text-2xl md:text-3xl text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700 }}>{s.title}</h3>
        <p className="mt-3 text-sm md:text-base c-muted leading-relaxed">{s.body}</p>
      </div>
    </motion.div>
  );
}

function SystemScroll() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const dotTop = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  return (
    <div ref={ref} className="relative max-w-3xl mx-auto" data-testid="fc-system-scroll">
      <div className="absolute left-[18px] md:left-[30px] top-0 bottom-0 w-px bg-white/10" />
      <motion.div className="absolute left-[18px] md:left-[30px] top-0 bottom-0 w-px bg-red-600 origin-top" style={{ scaleY: scrollYProgress }} />
      <motion.div className="absolute left-[18px] md:left-[30px] w-4 h-4 -translate-x-1/2 rounded-full bg-red-500" style={{ top: dotTop, boxShadow: "0 0 18px 5px rgba(239,35,60,0.65)" }} />
      {STAGES.map((s, i) => <SystemStage key={s.n} s={s} index={i} />)}
    </div>
  );
}

// ============================================================
// YouTube auto-play helper
// ============================================================
function loadYouTubeAPI() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (window._ytApiPromise) return window._ytApiPromise;
  window._ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve();
  });
  return window._ytApiPromise;
}


export default function ForCoachesPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const startRef = useRef(Date.now());
  const maxScrollRef = useRef(0);
  const maxVideoRef = useRef(0);
  const [muted, setMuted] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [variant] = useState(pickVariant);
  const activeVariant = HEADLINE_VARIANTS.find((v) => v.id === variant) || HEADLINE_VARIANTS[0];

  const saveEngagement = () => {
    try {
      sessionStorage.setItem("bc_fc_engagement", JSON.stringify({
        time_on_page: Math.round((Date.now() - startRef.current) / 1000),
        scroll_percent: maxScrollRef.current,
        video_watch_percent: maxVideoRef.current,
      }));
    } catch (e) { /* swallow */ }
  };

  useEffect(() => {
    document.title = "Elite Client Acquisition System — BrandChilly";
    try { sessionStorage.setItem("bc_booking_source", "ELITE"); } catch (e) { /* swallow */ }
    try {
      if (window.fbq) window.fbq("track", "ViewContent", { content_name: "ForCoachesElite", content_category: "Elite Funnel" });
      if (window.gtag) window.gtag("event", "view_content", { content_name: "ForCoachesElite" });
    } catch (e) { /* swallow */ }
    trackFunnel("page_view", PAGE, { dedupe: true, meta: { variant } });

    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total > 0) {
        const pct = Math.min(100, Math.round((window.scrollY / total) * 100));
        maxScrollRef.current = Math.max(maxScrollRef.current, pct);
        if (pct >= 25) trackFunnel("scroll_25", PAGE, { dedupe: true });
        if (pct >= 50) trackFunnel("scroll_50", PAGE, { dedupe: true });
        if (pct >= 75) trackFunnel("scroll_75", PAGE, { dedupe: true });
        if (pct >= 95) trackFunnel("scroll_100", PAGE, { dedupe: true });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const videoPoll = setInterval(() => {
      try {
        const v = videoRef.current;
        if (v && v.duration > 0) {
          const pct = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
          maxVideoRef.current = Math.max(maxVideoRef.current, pct);
          if (pct >= 25) trackFunnel("video_watch_25", PAGE, { dedupe: true });
          if (pct >= 50) trackFunnel("video_watch_50", PAGE, { dedupe: true });
          if (pct >= 75) trackFunnel("video_watch_75", PAGE, { dedupe: true });
        }
      } catch (e) { /* swallow */ }
    }, 2000);
    const saveTick = setInterval(saveEngagement, 5000);
    window.addEventListener("pagehide", saveEngagement);

    // Sticky mobile CTA — show after user scrolls past ~70vh (below the video)
    const onScrollSticky = () => {
      setShowStickyCta(window.scrollY > window.innerHeight * 0.7);
    };
    window.addEventListener("scroll", onScrollSticky, { passive: true });

    let cancelled = false;
    const v = videoRef.current;
    if (v) { try { v.muted = true; v.play().catch(() => {}); } catch (e) { /* swallow */ } }
    return () => {
      cancelled = true;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("scroll", onScrollSticky);
      window.removeEventListener("pagehide", saveEngagement);
      clearInterval(videoPoll);
      clearInterval(saveTick);
      saveEngagement();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (muted) { v.muted = false; v.volume = 0.9; if (v.paused) v.play().catch(() => {}); setMuted(false); }
      else { v.muted = true; setMuted(true); }
    } catch (e) { /* swallow */ }
  };

  const goToBook = () => {
    saveEngagement();
    trackFunnel("cta_click", PAGE, { meta: { variant } });
    try {
      sessionStorage.setItem("bc_booking_source", "ELITE");
      if (window.fbq) window.fbq("track", "Lead", { content_name: "ForCoachesElite", source: "ELITE" });
      if (window.gtag) window.gtag("event", "generate_lead", { content_name: "ForCoachesElite" });
    } catch (e) { /* swallow */ }
    navigate("/verify-your-eligibility");
  };

  return (
    <div className="coaches-page" style={{ minHeight: "100vh", background: "var(--c-bg)" }}>
      {/* HEADER */}
      <header className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-1 md:py-3 flex items-center justify-center">
          <img src="/assets/logo.png" alt="BrandChilly" className="h-20 md:h-44 w-auto" data-testid="fc-logo" />
        </div>
      </header>

      {/* SECTION 1: HERO */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-5 md:pt-16 pb-10 md:pb-14 text-center">
        <div
          className="inline-block c-eyebrow text-[10px] md:text-[11px] mb-5 md:mb-7 px-3 py-1.5 border border-red-700/40 text-red-400"
          style={{ borderRadius: 0 }}
          data-testid="fc-eyebrow"
        >
          For Established Coaches Only · By Invitation
        </div>
        <h1
          className="text-[32px] sm:text-5xl md:text-[58px] leading-[1.08] text-white"
          style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700 }}
          data-testid="fc-headline"
          data-ab-variant={variant}
        >
          You Know How To Get Results For Your Clients.
          <span className="block mt-3">You Don't Have A <span className="c-red">System</span> To Get The Clients.</span>
        </h1>
        <p className="mt-7 md:mt-9 max-w-2xl mx-auto text-base md:text-lg c-muted leading-relaxed" data-testid="fc-subheadline">
          If this looks like you, I suggest you watch this 5-minute breakdown to understand the
          framework changing <span className="text-white">2026 coaching acquisition dynamics.</span>
        </p>
      </section>

      {/* SECTION 2: VSL — autoplay muted, custom mute toggle */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pb-10 md:pb-16">
        <div
          className="mx-auto relative"
          style={{
            border: "1px solid rgba(192,41,26,0.35)",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.04), 0 30px 80px -20px rgba(192,41,26,0.35)",
          }}
          data-testid="fc-vsl-wrapper"
        >
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", background: "#000" }}>
            <video
              ref={videoRef}
              muted
              autoPlay
              playsInline
              preload="metadata"
              onPlay={() => trackFunnel("video_play", PAGE, { dedupe: true })}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
              data-testid="fc-vsl-player"
            >
              <source src="/assets/dse-vsl.webm" type="video/webm" />
              <source src={VSL_VIDEO_URL} type="video/mp4" />
            </video>
            {muted && (
              <button
                type="button"
                onClick={toggleMute}
                aria-label="Unmute video"
                data-testid="fc-tap-to-unmute"
                style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.0)", border: 0, cursor: "pointer", zIndex: 1 }}
              >
                <span
                  style={{
                    position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                    display: "inline-flex", alignItems: "center", gap: 10,
                    background: "rgba(0,0,0,0.7)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)", padding: "12px 22px",
                    fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase",
                  }}
                >
                  <VolumeX size={16} /> Tap to unmute
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              data-testid="fc-mute-toggle"
              style={{
                position: "absolute", right: 14, bottom: 14,
                width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(0,0,0,0.65)", color: "#fff",
                border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer", zIndex: 2,
              }}
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          </div>
        </div>

        {/* FIRST CTA — directly after the video */}
        <div className="mt-10 md:mt-12 text-center">
          <CtaButton testId="fc-cta-hero" onClick={goToBook} />
          <CtaSubtext />
        </div>
      </section>

      {/* Sentinel for sticky mobile CTA — placed right after the video section */}

      {/* THE 2026 REFRAME */}
      <section className="border-y border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">The 2026 Reframe</div>
            <h2 className="text-3xl md:text-5xl leading-[1.08] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>The Game Has Changed.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-testid="fc-reframe-grid">
            {REFRAME.map((r) => (
              <div key={r.title} className="border border-white/10 p-7 md:p-8 bg-black" style={{ borderRadius: 0 }}>
                <div className="text-5xl md:text-6xl c-red leading-none" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{r.stat}</div>
                <h3 className="mt-5 text-lg md:text-xl text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{r.title}</h3>
                <p className="mt-3 text-sm md:text-[15px] c-muted leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center"><CtaButton testId="fc-cta-reframe" onClick={goToBook} /><CtaSubtext /></div>
        </div>
      </section>

      {/* THE SOLUTION */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-4 text-center">
        <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">The Solution</div>
        <h2 className="text-3xl md:text-5xl leading-[1.08] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
          We Don’t Just Generate Leads. <span className="c-red italic block sm:inline">We Build the System.</span>
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-sm md:text-base c-muted">A comprehensive, end-to-end client acquisition vehicle — so you can stop guessing.</p>
      </section>

      {/* SHOW MY SYSTEM — vertical 3D scroll */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-12 md:pb-20">
        <div className="text-center mb-10 md:mb-16">
          <div className="c-eyebrow text-[10px] md:text-[11px] mb-3">The Machine</div>
          <h3 className="text-2xl md:text-4xl text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
            Six Stages. <span className="c-red italic">One System.</span>
          </h3>
          <p className="mt-4 text-sm md:text-base c-muted max-w-xl mx-auto">Scroll through the exact engine we deploy — a stranger enters at 01 and walks out at 06 as a client.</p>
        </div>
        <SystemScroll />
        <div className="mt-8 md:mt-10 text-center"><CtaButton testId="fc-cta-after-stages" onClick={goToBook} /><CtaSubtext /></div>
      </section>

      {/* DIGITAL SALES ENGINE FRAMEWORK — big bold statement */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-24 text-center">
        <div className="c-eyebrow text-[10px] md:text-[11px] mb-5">The Framework</div>
        <h2
          className="text-white"
          style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700, fontSize: "clamp(38px, 7vw, 88px)", lineHeight: 1.03 }}
          data-testid="fc-dse-framework"
        >
          This is the <span className="c-red">Digital Sales Engine</span> Framework.
        </h2>
      </section>

      {/* CLIENTS I HAVE WORKED ON */}
      <section className="border-y border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">Proven At Scale</div>
            <h2 className="text-3xl md:text-5xl leading-[1.08] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
              Clients We’ve <span className="c-red italic">Worked On.</span>
            </h2>
            <p className="mt-5 text-sm md:text-base c-muted">Brands we’ve helped scale — the same operating system now deployed for a select few coaches.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5" data-testid="fc-clients">
            {CLIENTS.map((c) => (
              <div key={c.name} className="bg-black p-7 md:p-9 flex flex-col items-start" style={{ borderRadius: 0 }}>
                <div className="text-4xl md:text-[52px] c-red leading-none" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700 }}>{c.revenue}</div>
                <div className="mt-4 text-base md:text-lg text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{c.name}</div>
                <div className="mt-1 text-[10px] tracking-[0.16em] uppercase text-zinc-500">{c.note}</div>
              </div>
            ))}
            <div className="bg-black p-7 md:p-9 flex flex-col items-center justify-center text-center" style={{ borderRadius: 0 }}>
              <div className="text-2xl md:text-3xl text-zinc-500 italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>and so on…</div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3b (NEW): IT'S NOT YOUR SERVICE - reframe + Is This You */}
      <section className="border-y border-white/5 bg-black/40">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">Hard Truth</div>
            <h2 className="text-3xl md:text-5xl leading-[1.08] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
              It’s Not Your Service. <span className="c-red italic block sm:inline">It’s Your System.</span>
            </h2>
            <p className="mt-6 text-sm md:text-base c-muted leading-relaxed">
              You’re genuinely good at what you do. Your clients get real results. But great work has never sold itself —
              and right now, getting your next client probably looks like this:
            </p>
          </div>

          <ul className="max-w-2xl mx-auto space-y-3 mb-10 md:mb-14" data-testid="fc-problems">
            {PROBLEMS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm md:text-base text-zinc-300">
                <X size={16} className="text-rose-500 mt-1 flex-shrink-0" /><span>{p}</span>
              </li>
            ))}
          </ul>

          <p className="max-w-3xl mx-auto text-center text-sm md:text-base text-zinc-300 leading-relaxed">
            Here’s the truth most coaches never hear: <span className="text-white">you don’t have a talent problem</span>,
            and you don’t have an effort problem. You have a <span className="c-red">system problem</span>.
            And unlike talent, a system can be built — fast.
          </p>

          {/* Is This You */}
          <div className="mt-14 md:mt-20 max-w-3xl mx-auto" data-testid="fc-is-this-you">
            <div className="text-center mb-8 md:mb-10">
              <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">Stop. Read This.</div>
              <h3 className="text-2xl md:text-4xl text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
                Is <span className="c-red italic">This You?</span>
              </h3>
            </div>
            <ul className="space-y-3 mb-8">
              {IS_THIS_YOU.map((q) => (
                <li key={q} className="flex items-start gap-3 text-sm md:text-base text-zinc-300">
                  <CheckCircle2 size={18} className="c-red mt-0.5 flex-shrink-0" /><span>{q}</span>
                </li>
              ))}
            </ul>
            <p className="text-center text-sm md:text-base text-white italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              If even two of these are true, this 30-minute call was built for you.
            </p>

            {/* CTA (NEW) */}
            <div className="mt-10 text-center">
              <CtaButton testId="fc-cta-is-this-you" onClick={goToBook} />
              <CtaSubtext />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: AUTHORITY STACK */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">The Authority Stack</div>
          <h2 className="text-3xl md:text-5xl leading-[1.08] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
            Driven by <span className="c-red italic">Cold Numbers,</span> Not Agency Fluff.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/5" data-testid="fc-authority-grid">
          {AUTHORITY.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.label} className="bg-black p-6 md:p-7 flex flex-col items-start" style={{ borderRadius: 0 }}>
                <Icon size={18} className="c-red mb-4" />
                <div className="text-3xl md:text-[40px] leading-none text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>{m.value}</div>
                <div className="mt-3 text-[11px] md:text-xs tracking-[0.14em] uppercase text-zinc-500 leading-snug">{m.label}</div>
              </div>
            );
          })}
        </div>

        <div
          className="mt-10 md:mt-14 max-w-3xl mx-auto bg-black/40 border border-white/10 p-6 md:p-8 flex flex-col md:flex-row items-start gap-6 md:gap-8"
          style={{ borderRadius: 0 }}
          data-testid="fc-pedigree"
        >
          <div className="flex-shrink-0 mx-auto md:mx-0">
            <img
              src="/assets/founder.jpg"
              alt="Saurabh Khaspuri — Founder, BrandChilly"
              className="block"
              style={{
                width: 132,
                height: 132,
                objectFit: "cover",
                filter: "grayscale(80%) contrast(1.08)",
                borderRadius: 0,
                border: "1px solid rgba(192,41,26,0.4)",
              }}
              data-testid="fc-pedigree-portrait"
            />
            <div className="text-[10px] tracking-[0.18em] uppercase text-center mt-3 c-red">
              Saurabh Khaspuri
            </div>
            <div className="text-[10px] tracking-[0.14em] uppercase text-center text-zinc-500">
              Founder · BrandChilly
            </div>
          </div>
          <div>
            <div className="c-eyebrow text-[10px] mb-3">About The Founder · The Enterprise Pedigree</div>
            <p className="text-sm md:text-base text-zinc-300 leading-relaxed">
              Led by <span className="c-red font-medium">Saurabh Khaspuri — ex-Chief Marketing Officer of Booming Bulls Academy</span>.
              Built on foundational leadership principles honed while executing strategies for global powerhouses and
              hyper-growth ecosystems — including{" "}
              <span className="text-white">HCL Technologies, IBM India, Wipro, Knowledgewoods, EIFS</span>.
            </p>
            <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed">
              16 years of on-the-ground operating experience across enterprise SaaS, edtech and creator economy —
              now deployed exclusively for a small portfolio of high-intent coaches.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4b: AD TRACK RECORD */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-16 md:pb-24" data-testid="fc-ad-proof">
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">Verified Ad Track Record</div>
          <h2 className="text-2xl md:text-4xl leading-[1.1] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
            Live screenshots. <span className="c-red italic">No agency math.</span>
          </h2>
          <p className="mt-4 text-sm md:text-base c-muted">Pulled directly from the dashboards. Real impressions, real spend, real conversions delivered for clients.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {AD_SCREENSHOTS.map((shot, i) => (
            <figure key={shot.url} className="bg-black/40 border border-white/10 overflow-hidden flex flex-col" style={{ borderRadius: 0 }} data-testid={`fc-ad-card-${i}`}>
              <div className="bg-white"><img src={shot.url} alt={shot.label} className="w-full h-auto block" loading="lazy" /></div>
              <figcaption className="p-5 border-t border-white/10">
                <div className="text-[10px] tracking-[0.18em] uppercase c-red mb-2">{shot.label}</div>
                <div className="text-sm text-zinc-300 leading-snug">{shot.metric}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* SECTION 6b (NEW): FAQs */}
      <section className="border-y border-white/5 bg-black/40">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="fc-faq">
          <div className="text-center mb-10 md:mb-14">
            <div className="c-eyebrow text-[10px] md:text-[11px] mb-4">Quick Questions</div>
            <h2 className="text-3xl md:text-5xl leading-[1.08] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
              Before you apply.
            </h2>
          </div>
          <ul className="divide-y divide-white/5 border border-white/10" style={{ borderRadius: 0 }}>
            {FAQS.map((f, i) => {
              const Icon = f.icon;
              const open = openFaq === i;
              return (
                <li key={f.q}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-5 text-left hover:bg-white/[0.03] transition"
                    data-testid={`fc-faq-q-${i}`}
                    aria-expanded={open}
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={16} className="c-red flex-shrink-0" />
                      <span className="text-sm md:text-base text-white">{f.q}</span>
                    </span>
                    <ChevronDown size={18} className={`text-zinc-500 flex-shrink-0 transition-transform ${open ? "rotate-180 text-red-500" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 md:px-7 pb-5 -mt-2 text-sm md:text-base c-muted leading-relaxed" data-testid={`fc-faq-a-${i}`}>
                      {f.a}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* SECTION 7: BOTTOM CTA */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="border border-white/10 bg-black/30 p-7 md:p-12 text-center" style={{ borderRadius: 0 }} data-testid="fc-gate-2">
          <h2 className="text-3xl md:text-5xl leading-[1.1] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
            Are you eligible to <span className="c-red italic">deploy this system?</span>
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-sm md:text-base c-muted leading-relaxed">
            Knowing the framework is straightforward. Constructing it perfectly — engineering the landing pages,
            configuring deep pixel attribution tracking, and managing optimized ad accounts — is where most execution fails.
          </p>
          <p className="mt-4 max-w-xl mx-auto text-sm md:text-base text-zinc-300">
            We do it completely for you. If you are accepted, our team deploys this entire system{" "}
            <span className="text-white">live into your coaching business.</span>
          </p>
          <div className="mt-10"><CtaButton testId="fc-cta-final" onClick={goToBook} /><CtaSubtext /></div>
        </div>
      </section>

      {/* ELIGIBILITY EVALUATION — moved to end of page */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <div className="border border-white/10 bg-black/30 p-7 md:p-12 text-center" style={{ borderRadius: 0 }} data-testid="fc-gate-1">
          <h2 className="text-2xl md:text-4xl leading-[1.15] text-white" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 600 }}>
            This is not a sales call.{" "}
            <span className="c-red italic block sm:inline">This is an eligibility evaluation.</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-sm md:text-base c-muted leading-relaxed">
            To maintain absolute delivery quality, we are brutally selective. We only partner with coaches who
            have verified product-market fit and are ready for professional-grade scale.
          </p>
          <ol className="mt-8 space-y-4 text-left max-w-md mx-auto">
            <li className="flex items-start gap-3"><CheckCircle2 size={18} className="c-red mt-0.5 flex-shrink-0" /><span className="text-sm md:text-base text-zinc-300"><span className="text-white">Step 1.</span> Tap below to begin your <span className="c-red">eligibility verification</span> — built to filter out non-serious inquiries.</span></li>
            <li className="flex items-start gap-3"><CheckCircle2 size={18} className="c-red mt-0.5 flex-shrink-0" /><span className="text-sm md:text-base text-zinc-300"><span className="text-white">Step 2.</span> Fill out the intensive application form carefully.</span></li>
            <li className="flex items-start gap-3"><CheckCircle2 size={18} className="c-red mt-0.5 flex-shrink-0" /><span className="text-sm md:text-base text-zinc-300"><span className="text-white">Step 3.</span> Our internal team audits your answers to see if your ecosystem matches our operational criteria.</span></li>
          </ol>
          <div className="mt-10"><CtaButton testId="fc-cta-primary" onClick={goToBook} /><CtaSubtext /></div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 pb-24 md:pb-8">
        <div className="max-w-6xl mx-auto px-5 md:px-8 text-center text-[11px] text-zinc-600 tracking-[0.18em] uppercase">
          BrandChilly · Personal Branding &amp; Business Growth · India
        </div>
      </footer>

      {/* STICKY MOBILE CTA — appears after user scrolls past the video */}
      {showStickyCta && (
        <div
          className="fixed bottom-0 inset-x-0 z-50 bg-black/95 backdrop-blur-xl border-t border-red-700/40 md:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
          data-testid="fc-sticky-mobile-cta"
        >
          <div className="flex items-stretch gap-2 p-3">
            <button
              type="button"
              onClick={goToBook}
              className="c-btn-primary flex-1 justify-center text-sm"
              style={{ minHeight: 52 }}
              data-testid="fc-sticky-cta"
            >
              Check If You Qualify <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
